import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Image,
  Link as ChakraLink,
  SimpleGrid,
  Text,
  VStack,
  Badge,
  Divider,
  useColorModeValue,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FiArrowLeft, FiSend } from 'react-icons/fi';
import { useBounty, incrementViewCount } from '../../hooks/useBounties';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { ReputationBadge } from '../common/ReputationBadge';
import { formatDate, RESEARCH_TYPES } from '../../utils/formatters';
import { Price } from '../common/Price';
import { maskReputation } from '../../utils/anonymizer';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function BountyDetail() {
  const { bountyId } = useParams();
  const { currentUser } = useAuth();
  const { bounty, loading } = useBounty(bountyId);
  const navigate = useNavigate();
  const toast = useToast();

  const [selectedImage, setSelectedImage] = useState(null);
  const [creatorReputation, setCreatorReputation] = useState(null);
  const [creatorIsAnonymous, setCreatorIsAnonymous] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const metaBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  useEffect(() => {
    if (bountyId) {
      incrementViewCount(bountyId).catch(() => {});
    }
  }, [bountyId]);

  useEffect(() => {
    if (bounty?.creatorUid) {
      getDoc(doc(db, 'users', bounty.creatorUid)).then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setCreatorReputation(data.reputation || null);
          setCreatorIsAnonymous(data.isFullyAnonymous === true);
        }
      });
    }
  }, [bounty?.creatorUid]);

  if (loading) return <LoadingSpinner message="Loading task..." />;
  if (!bounty) {
    return (
      <Container maxW="3xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>Task not found</Heading>
        <Button as={Link} to="/tasks" leftIcon={<Icon as={FiArrowLeft} />}>
          Back to Tasks
        </Button>
      </Container>
    );
  }

  const typeInfo = RESEARCH_TYPES[bounty.researchType] || { label: 'Research', color: 'gray' };
  const isOwner = currentUser?.uid === bounty.creatorUid;
  const canClaim = !isOwner && !['completed', 'cancelled'].includes(bounty.status);

  return (
    <Container maxW="5xl" py={6}>
      <VStack spacing={6} align="stretch">
        {/* Back */}
        <Button
          as={Link}
          to="/tasks"
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Tasks
        </Button>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {/* Main content */}
          <Box gridColumn={{ lg: 'span 2' }}>
            <VStack spacing={5} align="stretch">
              {/* Images */}
              {bounty.imageUrls?.length > 0 && (
                <Box rounded="xl" overflow="hidden" boxShadow="md" borderWidth={1} borderColor={borderColor}>
                  <Box bg="gray.50" display="flex" justifyContent="center" alignItems="center" minH="300px">
                    <Image
                      src={selectedImage || bounty.imageUrls[0]}
                      alt={bounty.title}
                      maxW="full"
                      maxH="480px"
                      objectFit="contain"
                    />
                  </Box>
                  {bounty.imageUrls.length > 1 && (
                    <HStack spacing={2} p={3} bg={cardBg} flexWrap="wrap">
                      {bounty.imageUrls.map((url, i) => (
                        <Image
                          key={i}
                          src={url}
                          alt={`Image ${i + 1}`}
                          h="64px"
                          w="64px"
                          objectFit="cover"
                          rounded="md"
                          cursor="pointer"
                          opacity={(selectedImage || bounty.imageUrls[0]) === url ? 1 : 0.55}
                          borderWidth={2}
                          borderColor={(selectedImage || bounty.imageUrls[0]) === url ? 'orange.400' : 'transparent'}
                          onClick={() => setSelectedImage(url)}
                        />
                      ))}
                    </HStack>
                  )}
                </Box>
              )}

              {/* Title & Description */}
              <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
                <VStack align="start" spacing={4}>
                  <HStack flexWrap="wrap" spacing={2}>
                    <Badge colorScheme={typeInfo.color} fontSize="sm" px={2} py={1} rounded="md">
                      {typeInfo.label}
                    </Badge>
                    <StatusBadge status={bounty.status} />
                  </HStack>

                  <Heading size="lg">{bounty.title}</Heading>

                  <Divider />

                  <Box>
                    <Text fontWeight="medium" mb={2} color="gray.700">Research Request</Text>
                    <Text color="gray.600" whiteSpace="pre-wrap" wordBreak="break-word" lineHeight="tall">
                      {bounty.description}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Sidebar */}
          <VStack spacing={4} align="stretch">
            {/* Reward & Action */}
            <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Reward</Text>
                  <Text fontSize="3xl" fontWeight="bold" color="green.600">
                    <Price cents={bounty.rewardAmount} />
                  </Text>
                </Box>

                {canClaim && (
                  <Button
                    as={Link}
                    to={`/tasks/${bountyId}/submit`}
                    colorScheme="orange"
                    size="lg"
                    leftIcon={<Icon as={FiSend} />}
                  >
                    Submit Research
                  </Button>
                )}

                {isOwner && (
                  <Button
                    as={Link}
                    to={`/my-submissions`}
                    variant="outline"
                    size="lg"
                  >
                    View My Submissions
                  </Button>
                )}

                {bounty.status === 'completed' && (
                  <Badge colorScheme="green" textAlign="center" py={2} fontSize="sm">
                    This task has been completed
                  </Badge>
                )}
              </VStack>
            </Box>

            {/* Metadata */}
            <Box bg={metaBg} rounded="xl" p={5} borderWidth={1} borderColor={borderColor}>
              <VStack align="start" spacing={3}>
                <Text fontWeight="medium" fontSize="sm">Task Details</Text>
                <Divider />
                <HStack justify="space-between" w="full">
                  <Text fontSize="sm" color="gray.500">Posted by</Text>
                  <VStack align="end" spacing={0}>
                    <ChakraLink
                      as={Link}
                      to={`/users/${bounty.creatorUid}`}
                      state={{ from: 'bounties' }}
                      fontSize="sm"
                      fontWeight="medium"
                      _hover={{ color: 'orange.500', textDecoration: 'underline' }}
                    >
                      {creatorIsAnonymous ? 'Anonymous' : bounty.creatorAnonymizedUsername}
                    </ChakraLink>
                    {creatorReputation && (
                      <ReputationBadge
                        reputation={creatorIsAnonymous ? maskReputation(creatorReputation) : creatorReputation}
                        size="xs"
                        anonymous={creatorIsAnonymous}
                      />
                    )}
                  </VStack>
                </HStack>
                <HStack justify="space-between" w="full">
                  <Text fontSize="sm" color="gray.500">Posted on</Text>
                  <Text fontSize="sm">{formatDate(bounty.createdAt)}</Text>
                </HStack>
                <HStack justify="space-between" w="full">
                  <Text fontSize="sm" color="gray.500">Views</Text>
                  <Text fontSize="sm">{bounty.viewCount || 0}</Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>

      </VStack>
    </Container>
  );
}
